import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGithub, FaShieldAlt, FaCode, FaLayerGroup } from 'react-icons/fa';
import './Projects.css';
import portfolioImg from '../../assets/portfolio.png';
import dinesmartImg from '../../assets/dinesmart.png';
import ebasketImg from '../../assets/e-basket.png';
import vmaxImg from '../../assets/vmax.png';
import paddlewarsImg from '../../assets/paddlewars.png';
import stonkwatchImg from '../../assets/stonkwatch.png';
import networkTopologyImg from '../../assets/network-topology.jpg';
import wazuhSiemImg from '../../assets/wazuh-siem.jpg';
import networkMonitoringImg from '../../assets/network-monitoring.jpg';
import nmapScannerImg from '../../assets/nmap-scanner.jpg';

const Projects = () => {
  const [activeFilter, setActiveFilter] = useState('ALL');

  const filterOptions = [
    { id: 'ALL', label: 'ALL', icon: <FaLayerGroup /> },
    { id: 'networking', label: 'Networking & Security Projects', icon: <FaShieldAlt /> },
    { id: 'development', label: 'Development Projects', icon: <FaCode /> }
  ];

  const projects = [
    // Networking & Security Projects (Primary Role)
    {
      id: 1,
      title: 'Enterprise Network & Security Topology',
      description: 'Simulated enterprise network architecture with multi-area OSPF, inter-VLAN routing, ACL security filters, and DHCP snooping in Cisco Packet Tracer.',
      image: networkTopologyImg,
      github: 'https://github.com/AryanKhanal03',
      category: 'networking',
      tags: ['Cisco Packet Tracer', 'VLANs / OSPF', 'ACLs', 'PuTTY / SSH']
    },
    {
      id: 2,
      title: 'Wazuh SIEM Threat Monitoring Lab',
      description: 'Configured Wazuh SIEM environment for endpoint threat monitoring, real-time intrusion detection, syslog correlation, and incident alerting.',
      image: wazuhSiemImg,
      github: 'https://github.com/AryanKhanal03',
      category: 'networking',
      tags: ['Wazuh SIEM', 'Threat Detection', 'Syslog', 'Security Operations']
    },
    {
      id: 3,
      title: 'Network Telemetry & Monitoring System',
      description: 'Comprehensive network monitoring deployment using LibreNMS and Zabbix for SNMP telemetry, automated threshold alerts, and bandwidth analysis.',
      image: networkMonitoringImg,
      github: 'https://github.com/AryanKhanal03',
      category: 'networking',
      tags: ['LibreNMS', 'Zabbix', 'SNMP Telemetry', 'Alerting']
    },
    {
      id: 4,
      title: 'Nmap Vulnerability Recon & Scanner Automation',
      description: 'Automated network port scanning, service detection, and vulnerability audit scripting using Nmap and Python for security assessments.',
      image: nmapScannerImg,
      github: 'https://github.com/AryanKhanal03',
      category: 'networking',
      tags: ['Nmap', 'Python', 'Port Scanning', 'Security Audit']
    },
    // Development Projects (Sub Role)
    {
      id: 5,
      title: 'Portfolio Website',
      description: 'Personal portfolio website built with React and Framer Motion with glassmorphism design.',
      image: portfolioImg,
      github: 'https://github.com/AryanKhanal03/Portfolio-website',
      category: 'development',
      tags: ['JavaScript (React)', 'CSS3', 'Framer Motion']
    },
    {
      id: 6,
      title: 'DineSmart',
      description: 'A restaurant management system for seamless dining experiences and order workflows.',
      image: dinesmartImg,
      github: 'https://github.com/AryanKhanal03/DineSmart',
      category: 'development',
      tags: ['Flutter (Dart)', 'Firebase']
    },
    {
      id: 7,
      title: 'E-Basket',
      description: 'E-commerce platform for online shopping with dynamic cart functionality and user auth.',
      image: ebasketImg,
      github: 'https://github.com/AryanKhanal03/E_Basket-1',
      category: 'development',
      tags: ['JavaScript (React)', 'Firebase', 'CSS3']
    },
    {
      id: 8,
      title: 'V-Max',
      description: 'Robust backend for the V-Max application focusing on high performance and clean API endpoints.',
      image: vmaxImg,
      github: 'https://github.com/AryanKhanal03/V-Max',
      category: 'development',
      tags: ['Java (SpringBoot)', 'JavaScript (React)', 'MySQL']
    },
    {
      id: 9,
      title: 'Paddle Wars',
      description: 'A classic pong arcade game reimagined using OpenGL and Python graphics pipeline.',
      image: paddlewarsImg,
      github: 'https://github.com/AryanKhanal03/paddle-wars.py',
      category: 'development',
      tags: ['Python (OpenGL)', 'Game Dev']
    },
    {
      id: 10,
      title: 'Stonk Watch',
      description: 'Real-time stock market tracking application for investors with interactive financial charting.',
      image: stonkwatchImg,
      github: 'https://github.com/AryanKhanal03/Stonk-Watch',
      category: 'development',
      tags: ['Flutter (Dart)', 'REST API']
    }
  ];

  const filteredProjects = activeFilter === 'ALL'
    ? projects
    : projects.filter((project) => project.category === activeFilter);

  return (
    <section id="projects" className="projects section">
      <div className="container">
        <div className="section-title">
          <h2>My Projects</h2>
        </div>

        {/* Category Filter Tabs */}
        <div className="project-filters">
          {filterOptions.map((option) => (
            <button
              key={option.id}
              className={`filter-btn glass ${activeFilter === option.id ? 'active' : ''}`}
              onClick={() => setActiveFilter(option.id)}
            >
              <span className="filter-icon">{option.icon}</span>
              <span className="filter-text">{option.label}</span>
              {activeFilter === option.id && (
                <motion.div 
                  className="filter-active-pill" 
                  layoutId="activeFilterPill"
                  transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                />
              )}
            </button>
          ))}
        </div>
        
        <motion.div 
          className="projects-grid"
          layout
        >
          <AnimatePresence>
            {filteredProjects.map((project) => (
              <motion.div 
                key={project.id} 
                className="project-card glass"
                layout
                initial={{ opacity: 0, scale: 0.9, y: 20 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.9, y: 20 }}
                transition={{ duration: 0.4 }}
                whileHover={{ y: -10 }}
              >
                <div className="project-image">
                  <img src={project.image} alt={project.title} />
                  <div className="project-category-tag">
                    {project.category === 'networking' ? 'Network & Security' : 'Development'}
                  </div>
                  <div className="project-overlay">
                    <div className="project-links">
                      <a href={project.github} target="_blank" rel="noopener noreferrer" className="icon-link" aria-label="GitHub Repository">
                        <FaGithub />
                      </a>
                    </div>
                  </div>
                </div>
                <div className="project-info">
                  <div className="project-tags">
                    {project.tags.map((tag, index) => (
                      <span key={index} className="tag">{tag}</span>
                    ))}
                  </div>
                  <h3>{project.title}</h3>
                  <p>{project.description}</p>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </motion.div>
      </div>
    </section>
  );
};

export default Projects;